<template>
  <div class="support-chat" :class="{ 'support-chat--task': taskMode }">
    <SupportFaqView v-if="mode === 'faq'" @close="mode = 'chat'" />
    <template v-else>
    <!-- Help/docs entry row — pinned at the top of the chat feed; a task's own feed has none (it is the task). -->
    <button v-if="!taskMode && !hideFaqLink" class="support-chat__faq-link" type="button" @click="mode = 'faq'">
      <span>{{ t('vitrine.faq.entry') }}</span>
      <span class="support-chat__faq-chevron" aria-hidden="true">›</span>
    </button>
    <!-- In a conversation the other channels stay pinned under the FAQ row; without one they close the welcome. -->
    <SupportContactLinks v-if="ticket && !taskMode" />

    <!--
      Conversation list (B1/B3). Active threads as chips — shown only when there is something to switch to: more
      than one active thread, or past ones (DVL-028).

      Without this list an operator message in a non-active thread does not exist for the client: the "+1"
      badge is there, but where to go is not. The issuance request and the top-up request each live in their
      own thread, reachable only from "their" screen — leave it and there was no way back.
      The cabinet's concierge tab has no strip (prototype D): its threads live in the history behind the clock.
    -->
    <nav v-if="!taskMode && !hideThreadList && (threads.length > 1 || pastThreads.length)" class="support-chat__threads" :aria-label="t('support.title')">
      <template v-for="chip in threadChips" :key="chip.key">
        <button
          v-if="chip.kind === 'thread'"
          type="button"
          class="support-chat__thread"
          :class="{ 'support-chat__thread--active': !showPast && !openGroupKey && chip.thread.id === activeTicketId }"
          @click="selectThread(chip.thread.id)"
        >
          <span class="support-chat__thread-label">{{ threadLabel(chip.thread) }}</span>
          <span v-if="unreadByTicket[chip.thread.id]" class="support-chat__thread-badge">{{ unreadByTicket[chip.thread.id] }}</span>
        </button>
        <!-- One chip per member who asks (E2E family office Б-6): it opens the list of their requests. -->
        <button
          v-else
          type="button"
          class="support-chat__thread"
          :class="{ 'support-chat__thread--active': !showPast && (openGroupKey === chip.key
            || (!openGroupKey && chip.threads.some(x => x.id === activeTicketId))) }"
          data-testid="support-money-group" :data-count="chip.threads.length"
          @click="toggleGroup(chip.key)"
        >
          <span class="support-chat__thread-label">{{ t('cabinet.money.chipCount',
            { title: t(chip.title.key, chip.title.params ?? {}), count: chip.threads.length }) }}</span>
          <span v-if="groupUnread(chip.threads)" class="support-chat__thread-badge">{{ groupUnread(chip.threads) }}</span>
        </button>
      </template>
      <button
        v-if="pastThreads.length"
        type="button"
        class="support-chat__thread"
        :class="{ 'support-chat__thread--active': showPast || ticketIsPast }"
        data-testid="support-past-toggle"
        @click="showPast = !showPast; openGroupKey = null"
      >
        <span class="support-chat__thread-label">{{ t('support.past.title') }} · {{ pastThreads.length }}</span>
      </button>
    </nav>
    <div ref="scrollEl" class="support-chat__body">
      <div v-if="initialLoading" class="support-chat__loading">
        <div class="spinner" />
      </div>
      <!-- DVL-028: past conversations — open one to read it, write to reopen it. -->
      <!-- The requests of one member (their chip): each opens its own thread and card. -->
      <ul v-else-if="openGroup" class="support-chat__past" data-testid="support-money-group-list">
        <li class="support-chat__group-hint">{{ t('cabinet.money.groupHint', { count: openGroup.threads.length }) }}</li>
        <li v-for="th in openGroup.threads" :key="th.id">
          <button type="button" class="support-chat__past-row" :data-testid="`support-money-group-row-${th.id}`"
                  @click="selectThread(th.id)">
            <span class="support-chat__past-label">{{ groupRowLabel(th) }}</span>
            <span class="support-chat__past-meta">
              {{ d(new Date(th.lastMessageAt), 'short') }}
              <span v-if="unreadByTicket[th.id]" class="support-chat__thread-badge">{{ unreadByTicket[th.id] }}</span>
            </span>
          </button>
        </li>
      </ul>
      <ul v-else-if="showPast" class="support-chat__past" data-testid="support-past-list">
        <li v-for="th in pastThreads" :key="th.id">
          <button type="button" class="support-chat__past-row" :data-testid="`support-past-${th.id}`"
                  @click="selectThread(th.id)">
            <span class="support-chat__past-label">{{ threadLabel(th) }}</span>
            <span class="support-chat__past-meta">
              {{ d(new Date(th.lastMessageAt), 'short') }} · {{ t(`support.past.status.${th.status}`) }}
            </span>
          </button>
        </li>
      </ul>
      <template v-else>
        <!--
          No conversation yet — the welcome (B2 REQ-043): who will reply (the manager on duty, a real photo or none),
          or the plain hint; then the suggested questions, which fill the field and are not sent by themselves.
        -->
        <!-- A thread that was asked for by id and failed to load (the core restarting): say so, never the general
             chat's «Чем можем помочь?» over somebody's task (cabinet E2E 31.10, Б-7, 02 §2.12). -->
        <DsError v-if="loadFailed" :title="t('support.threadLoadFailed')" data-testid="support-load-failed"
                 @retry="activate()" />
        <!-- The cabinet's concierge (prototype D, 09 §Д-5.2): «Добрый день, <имя>» in the middle, «Чем можем помочь?»,
             then the action pills (the screen's slot) and the server's suggestions in the same pills. Who replies is
             in the screen's header; no hint paragraph, no channel list (owner's simplicity rule). -->
        <section v-else-if="!ticket && greeting" class="welcome welcome--greeting" data-testid="support-welcome"
                 :data-loaded="welcomeLoaded ? 'true' : 'false'">
          <h2 class="welcome__greeting" data-testid="support-greeting">
            <template v-for="(line, i) in greeting.split('\n')" :key="i"><br v-if="i">{{ line }}</template>
          </h2>
          <p class="welcome__question">{{ t('support.welcome.title') }}</p>
          <div class="welcome__prompts">
            <slot name="welcome-actions" />
            <button v-for="s in shownSuggestions" :key="s.id" type="button" class="welcome__prompt"
                    data-testid="support-suggestion" @click="useSuggestion(s.text)">{{ s.text }}</button>
          </div>
        </section>
        <section v-else-if="!ticket" class="welcome" data-testid="support-welcome"
                 :data-loaded="welcomeLoaded ? 'true' : 'false'">
          <h2 class="welcome__title">{{ t('support.welcome.title') }}</h2>
          <div v-if="welcomeManager.mode === 'DUTY' && welcomeManager.name" class="welcome__manager"
               data-testid="welcome-manager">
            <ManagerPhoto :token="welcomeManager.photoToken" :name="welcomeManager.name" :size="48" />
            <span>{{ t('support.welcome.replyFrom', { name: welcomeManager.name }) }}</span>
          </div>
          <p v-else class="support-chat__empty">{{ t('support.firstMessageHint') }}</p>
          <template v-if="shownSuggestions.length">
            <div class="welcome__label">{{ t('support.welcome.suggestions') }}</div>
            <div class="welcome__suggestions">
              <button v-for="s in shownSuggestions" :key="s.id" type="button" class="welcome__suggestion"
                      data-testid="support-suggestion" @click="useSuggestion(s.text)">
                {{ s.text }}
              </button>
            </div>
          </template>
          <!-- REQ-043: the contact channels (DVL-010) come last, below the suggestions. -->
          <SupportContactLinks class="welcome__contacts" />
        </section>
        <p v-else-if="ticketIsPast" class="support-chat__past-note" data-testid="support-past-note">
          {{ ticket.status === 'CLOSED' ? t('support.past.closedNote') : t('support.past.reopenNote') }}
        </p>
        <!-- What the screen hosting the feed puts over it (the task's status chip). -->
        <slot name="feed-top" />
        <ul v-if="ticket" class="support-chat__messages">
          <!-- A concierge system line (§5.2): in a task's feed a concierge bubble; elsewhere compact and centred. -->
          <li v-for="(m, i) in shownMessages" :key="m.id" :class="lineClass(m)" :data-message-id="m.id">
            <template v-if="isEventLine(m)">
              <ChatBlockCard :block="m.block" :state="blockStates[m.id]" />
              <small v-if="taskMode" class="msg__by" data-testid="msg-by">{{ byLabel(m) }}</small>
            </template>
            <template v-else>
            <!-- The manager's name over the first bubble of their run, as in messengers (B2 REQ-042); a system card is
                 signed by the concierge. A task's feed signs every bubble at its foot instead (the design). -->
            <span v-if="!taskMode && startsSignedRun(i)" class="msg__author" data-testid="msg-author">{{ m.operator?.name }}</span>
            <span v-else-if="!taskMode && m.kind === 'BLOCK' && !m.operator" class="msg__author" data-testid="msg-author-system">
              {{ t('vitrine.concierge.author.concierge') }}
            </span>
            <ChatBlockCard
              v-if="m.kind === 'BLOCK' && m.block"
              :ref="el => setCardRef(m.id, el)"
              :block="m.block"
              :state="blockStates[m.id]"
              :task="task"
              :busy="sending || answering"
              :error="blockErrors[m.id]"
              @answer="text => answerBlock(m.id, text)"
              :reread="rereadCards"
              @attach="fileInput?.click()"
            />
            <template v-else-if="m.attachment">
              <img
                v-if="isImage(m.attachment) && attachmentUrls[m.attachment.id]"
                :src="attachmentUrls[m.attachment.id]"
                class="msg__image"
                :alt="m.attachment.filename ?? t('support.attachment')"
                @click="openImage(m.attachment)"
              >
              <VoiceMessage
                v-else-if="isAudio(m.attachment) && attachmentUrls[m.attachment.id]"
                :src="attachmentUrls[m.attachment.id]"
                class="msg__voice"
              />
              <button
                v-else
                type="button" class="msg__file"
                @click="isImage(m.attachment) ? openImage(m.attachment) : downloadAttachment(m.attachment)"
              >
                <span class="msg__file-name">{{ m.attachment.filename ?? t('support.attachment') }}</span>
                <span class="msg__file-size">{{ formatSize(m.attachment.byteSize) }}</span>
              </button>
            </template>
            <span v-if="m.text" class="msg__text"><template v-for="(part, pi) in messageParts(m.text)" :key="pi"><router-link
              v-if="part.kind === 'link'" :to="part.path" class="msg__link" data-testid="msg-cabinet-link">{{ part.text }}</router-link><template
              v-else>{{ part.text }}</template></template></span>
            <!-- A card carries no signature in the design; a bubble is signed at its foot. -->
            <small v-if="taskMode && m.kind !== 'BLOCK'" class="msg__by" data-testid="msg-by">{{ byLabel(m) }}</small>
            <span v-else-if="!taskMode" class="msg__time">{{ d(new Date(m.createdAt), 'time') }}</span>
            </template>
          </li>
        </ul>
      </template>
    </div>

    <!-- An attachment waiting to go: a small chip above the field (DVL-004), plus any error. -->
    <div v-if="!showPast && !openGroup && (pendingFile || fileError || recordError || intakeError)" class="support-chat__pending">
      <span v-if="pendingFile" class="support-chat__chip" data-testid="support-pending-file">
        <span class="support-chat__chip-name">{{ pendingFile.name }}</span>
        <span class="support-chat__chip-size">{{ formatSize(pendingFile.size) }}</span>
        <button type="button" class="support-chat__chip-clear" :aria-label="t('support.composer.removeFile')"
                @click="clearPendingFile">×</button>
      </span>
      <span v-if="fileError" class="support-chat__file-error">{{ fileError }}</span>
      <span v-if="recordError" class="support-chat__file-error">{{ recordError }}</span>
      <span v-if="intakeError" class="support-chat__file-error" role="alert" data-testid="support-intake-error">{{ intakeError }}</span>
    </div>
    <!--
      Messenger-style composer (DVL-004): a full-width pill with the paperclip inside, and ONE round button on the
      right — a microphone while the field is empty, a send arrow once there is text or a file. While recording, the
      pill becomes a recording bar (red dot, timer, Cancel) and the round button sends the clip.
    -->
    <!-- Not under the past-conversations list: a message typed there would go to a thread that is not on screen. -->
    <!-- One file picker for both composers and the «Прикрепить» of a document request. -->
    <input
      ref="fileInput"
      type="file"
      :accept="intakeArmed ? INTAKE_ACCEPT : CHAT_ACCEPT"
      class="support-chat__file-input"
      data-testid="support-file-input"
      @change="onFilePicked"
    >
    <!--
      A task's feed uses the same messenger composer as every chat (prototype m-master-chat): the paperclip inside the
      pill, one round button — mic while empty, send once there is text or a file. The task's own actions (summary,
      repeat, cancel) open from the status badge in the task's header, not from the composer.
    -->
    <!-- `data-intake="armed"`: the host typed a request draft («Хочу оплатить счёт»); sending it files a task. -->
    <form v-show="!showPast && !openGroup" class="support-chat__compose" data-testid="support-composer"
          :data-intake="intakeArmed ? 'armed' : undefined" @submit.prevent="onPrimary">
      <div v-if="recording" class="support-chat__recbar" data-testid="support-recording">
        <button type="button" class="support-chat__rec-cancel" @click="cancelRecording">
          {{ t('support.composer.cancel') }}
        </button>
        <span class="support-chat__rec-dot" aria-hidden="true" />
        <span class="support-chat__rec-time" :aria-label="t('support.composer.recording')">{{ recordClock }}</span>
      </div>
      <div v-else class="support-chat__pill" :class="{ 'support-chat__pill--disabled': composerLocked }">
        <button
          type="button" class="support-chat__icon-btn"
          :disabled="sending || composerLocked"
          :aria-label="t('support.composer.attach')"
          :title="t('support.composer.attach')"
          data-testid="support-attach"
          @click="fileInput?.click()"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.4a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </button>
        <textarea
          ref="inputEl"
          v-model="draft"
          rows="1"
          maxlength="4000"
          class="support-chat__input"
          :placeholder="composerPlaceholder"
          :aria-label="composerPlaceholder"
          :disabled="sending || composerLocked"
          data-testid="support-input"
          @input="autoGrow"
          @keydown.enter="onEnter"
        />
      </div>
      <button
        type="submit"
        class="support-chat__round"
        :class="{ 'support-chat__round--send': showSendArrow }"
        :disabled="primaryDisabled"
        :aria-label="showSendArrow ? t('support.composer.send') : t('support.composer.record')"
        :title="showSendArrow ? t('support.composer.send') : t('support.composer.record')"
        :data-testid="showSendArrow ? 'support-send' : 'support-record'"
      >
        <svg v-if="showSendArrow" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
        <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
             stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3" />
        </svg>
      </button>
    </form>
    </template>
  </div>
</template>

<script setup lang="ts">
import { splitCabinetLinks } from '@/utils/cabinetLinks';
import { computed, nextTick, onActivated, onDeactivated, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DEFAULT_MANAGER,
  supportApi,
  supportAttachmentUrl,
  type Attachment,
  type BlockState,
  type ChatBlock,
  type ManagerView,
  type Message,
  type MessagesPage,
  type WelcomeSuggestion,
  type Ticket,
} from '@/api/support';
import { useSupport } from '@/composables/useSupport';
import SupportContactLinks from './SupportContactLinks.vue';
import SupportFaqView from './SupportFaqView.vue';
import VoiceMessage from './VoiceMessage.vue';
import ManagerPhoto from './ManagerPhoto.vue';
import ChatBlockCard from './ChatBlockCard.vue';
import DsError from '@/components/ds/DsError.vue';
import { hasMovingBlock, isConciergeThread, isRenderableBlock, overlapCursor, unseen } from './chatBlocks';
import { moneyRequestsApi, type MoneyRequestView } from '@/api/moneyRequests';
import { cardActionsApi, type CardActionView } from '@/api/cardActions';
import { cardActionThreadTitle } from '@/views/cabinet/concierge/threads';
import { groupThreadChips, moneyThreadTitle, requestStateKey, type ThreadChip } from '@/views/cabinet/members/memberUi';
import { formatDisplayAmount } from '@/utils/money';
import type { ConciergeRequestView } from '@/api/concierge';
import { errorCodeOf, INTAKE_ACCEPT, intakeFileProblem } from '@/views/vitrine/concierge/conciergeUi';

// active = the chat is visible/active (drawer open, or the mounted full-page view).
// Polling runs only while active, so a hidden drawer never hits the network.
// taskMode = the feed of a concierge task's screen (CONCIERGE_CHAT_FIRST.md §5.2): bound to the task's thread, no FAQ,
// no conversation list, a steady 5 s poll; `task` is what the cards of that task read.
const props = withDefaults(
  defineProps<{
    active?: boolean; ticketId?: string | null; initialMode?: 'chat' | 'faq';
    taskMode?: boolean; task?: ConciergeRequestView | null;
    /** The cabinet's concierge tab: help lives in the profile, not on top of the chat (02 §3.3). */
    hideFaqLink?: boolean;
    /** «Добрый день,\n<имя>» — the cabinet's welcome of prototype D (lines split on «\n»); null — the plain welcome. */
    greeting?: string | null;
    /** The composer's placeholder; null — «Сообщение». */
    placeholder?: string | null;
    /** No conversation strip over the feed: the host lists the threads itself (the cabinet's history, prototype D). */
    hideThreadList?: boolean;
    /**
     * The composer holds a request draft the host typed in (prototype 064: «Оплатить счёт» puts «Хочу оплатить счёт»
     * into this field). While armed, sending hands the text and the attached file to `intakeSubmit` instead of posting
     * a chat message; clearing the field disarms it (`update:intakeArmed` false) and the composer is a chat again.
     */
    intakeArmed?: boolean;
    /** Files the armed draft; resolves to an error text to show under the field, or null when it went through. */
    intakeSubmit?: ((text: string, files: File[]) => Promise<string | null>) | null;
  }>(),
  {
    active: true, ticketId: null, initialMode: 'chat', taskMode: false, task: null, hideFaqLink: false, greeting: null,
    placeholder: null, hideThreadList: false, intakeArmed: false, intakeSubmit: null,
  },
);

// Internal mode: chat feed | help page (Q&A + documents). No routing — works in the drawer and full-page.
// initialMode lets a caller open straight on the FAQ list (Settings → FAQ deep-link).
const mode = ref<'chat' | 'faq'>(props.initialMode);
const emit = defineEmits<{
  'update:manager': [m: ManagerView];
  /** A card arrived or a card's state changed — the task screen re-reads the task its cards point at. */
  'blocks-changed': [];
  /** The armed request draft was cleared by the client or filed: the composer is a plain chat again. */
  'update:intakeArmed': [armed: boolean];
}>();
const { t, d, locale } = useI18n();

const ticket = ref<Ticket | null>(null);
const messages = ref<Message[]>([]);
/** A card this build does not know is left out; text bubbles and attachments always show. */
const shownMessages = computed(() => messages.value.filter(m => m.kind !== 'BLOCK' || isRenderableBlock(m)));

/** The cards' state for the whole thread, replaced by every page (§5.2 «Обновление без перезагрузки»). */
const blockStates = ref<Record<string, BlockState>>({});
/** The thread asked for by id could not be loaded (Б-7): the feed shows an error with a retry. */
const loadFailed = ref(false);
let blockStatesSig = '';
function applyBlockStates(page: MessagesPage, newItems: Message[] = []) {
  const next = page.blockStates;
  const sig = JSON.stringify(next);
  const changed = sig !== blockStatesSig || newItems.some(m => m.kind === 'BLOCK');
  blockStatesSig = sig;
  blockStates.value = next;
  if (changed) emit('blocks-changed');
}

/** Per-card refusal text and the card components (to clear a filled field once the answer is accepted). */
const blockErrors = reactive<Record<string, string>>({});
const cardRefs = new Map<string, { clear: () => void }>();
function setCardRef(id: string, el: unknown) {
  if (el && typeof (el as { clear?: unknown }).clear === 'function') cardRefs.set(id, el as { clear: () => void });
  else cardRefs.delete(id);
}
const answering = ref(false);

/**
 * The client answers a QUESTION (an option's text) or a REQUEST_INPUT (the field) — an ordinary message of theirs
 * with `replyToMessageId` (§5.2). The card closes when the next page says it is answered.
 */
async function answerBlock(messageId: string, text: string) {
  const id = ticket.value?.id;
  if (!id || answering.value || sending.value) return;
  answering.value = true;
  delete blockErrors[messageId];
  try {
    await supportApi.send(text, { ticketId: id, replyToMessageId: messageId });
    cardRefs.get(messageId)?.clear();
    await refreshFeed(id);
  } catch (e) {
    blockErrors[messageId] = errorCodeOf(e) === 'INVALID_REPLY_TO'
      ? t('vitrine.concierge.block.replyStale')
      : t('vitrine.concierge.block.replyFailed');
    void refreshFeed(id).catch(() => { /* the next poll re-reads the card */ });
  } finally {
    answering.value = false;
  }
}

/**
 * A concierge card made its call (or was refused as stale): the feed is read again so every card shows its new state,
 * and the task screen re-reads the task (the status chip, the «…» summary).
 */
async function rereadCards(): Promise<void> {
  const id = ticket.value?.id;
  if (!id) return;
  try {
    await refreshFeed(id);
  } catch { /* the next poll re-reads the cards */ }
  emit('blocks-changed');
}

/** Who a bubble of a task's feed is from, at its foot: «Вы», the manager, or the concierge itself. */
function byLabel(m: Message): string {
  if (m.direction === 'CUSTOMER') return t('vitrine.concierge.author.you');
  return m.operator?.name ?? t('vitrine.concierge.author.concierge');
}

/** A system line, an operator bubble (or card), or the client's bubble. */
/** A system status line (a concierge event, a top-up status other than the closing card): compact, not a bubble. */
function isEventLine(m: Message): m is Message & { block: ChatBlock } {
  if (m.kind !== 'BLOCK' || !m.block) return false;
  return m.block.type === 'CONCIERGE_EVENT' || (m.block.type === 'TOPUP_EVENT' && m.block.event !== 'COMPLETED');
}
function lineClass(m: Message): string {
  if (isEventLine(m)) return props.taskMode ? 'msg msg--operator' : 'msg-system';
  return `msg msg--${m.direction.toLowerCase()}${m.kind === 'BLOCK' ? ' msg--card' : ''}`;
}
// Shared draft (useSupport) so "Top up" can prefill the composer before the chat opens. Only the general chat reads
// it: a task's composer keeps its own, so a prefilled «freeze my card» never lands in a payment task's thread (02 §3.4).
const shared = useSupport();
const { refreshUnread, recountUnread, unreadByTicket } = shared;
const draft = props.taskMode ? ref('') : shared.supportDraft;
const composerPlaceholder = computed(() => props.placeholder
  ?? t(props.taskMode ? 'vitrine.concierge.composer.placeholder' : 'support.composer.placeholder'));

/** Only links to the cabinet itself become tap targets (utils/cabinetLinks); everything else stays text. */
function messageParts(text: string) {
  return splitCabinetLinks(text, window.location.origin);
}

/**
 * The client's conversations and which one is selected (B1/B3).
 *
 * `activeTicketId` is what the client picked by hand; until they pick, it is null and the default rule applies
 * (the bound thread when the component is tied to a request, otherwise GENERAL).
 */
const allThreads = ref<Ticket[]>([]);
/** The general chat leaves concierge task threads out (CF-021) — they live on the task's screen; the bound one stays. */
function visibleThreads(owned: Ticket[]): Ticket[] {
  return owned.filter(th => !isConciergeThread(th) || th.id === props.ticketId);
}
const activeTicketId = ref<string | null>(null);

const isLive = (th: Ticket) => th.status === 'OPEN' || th.status === 'ASSIGNED';
/** Conversations in progress — the chips. */
const threads = computed(() => allThreads.value.filter(isLive));
/** Finished conversations, newest first (DVL-028): readable, and a reply reopens one. */
const pastThreads = computed(() => allThreads.value.filter(th => !isLive(th))
  .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt)));
/** The "past conversations" list is on screen instead of a thread. */
const showPast = ref(false);
const ticketIsPast = computed(() => !!ticket.value && !isLive(ticket.value));
/** CLOSED is final on the server (409) — the composer says so instead of failing. RESOLVED reopens on reply. */
const composerLocked = computed(() => ticket.value?.status === 'CLOSED');

/** A human name for the conversation. `subject` won't do: for request threads it is "Card issuance #A1B2" — an internal tag. */
function threadLabel(th: Ticket): string {
  const money = moneyThreadTitle(th, moneyRequests.value, t('cabinet.money.someone'));
  if (money) return t(money.key, money.params ?? {});
  if (th.refType === 'CARD_CHANGE') return cardActionThreadTitle(th.refId ? cardActions.value.get(th.refId) : null, t);
  return t(`support.thread.${th.refType ?? 'GENERAL'}`);
}

/**
 * The money requests behind MONEY_REQUEST threads (docs/cabinet-redesign/07), so their chips read «Анна Иванова
 * просит» / «Ваш запрос · Иван Петров» instead of two identical «Запрос суммы». Read only when such a thread exists.
 */
const moneyRequests = ref<Map<string, MoneyRequestView>>(new Map());
watch(() => allThreads.value.filter(x => x.refType === 'MONEY_REQUEST' && x.refId && !moneyRequests.value.has(x.refId)).length,
  missing => {
    if (!missing) return;
    moneyRequestsApi.mine()
      .then(list => { moneyRequests.value = new Map(list.map(m => [m.id, m])); })
      .catch(() => { /* the plain «Запрос суммы» stays */ });
  });

/**
 * The chips: each live thread, except that the money-request threads of one member and side share one chip with their
 * count (E2E family office Б-6 — seven identical «Анна Петрова просит» in a row).
 */
const threadChips = computed<ThreadChip<Ticket>[]>(() =>
  groupThreadChips(threads.value, moneyRequests.value, t('cabinet.money.someone')));
/** The group whose request list is on screen instead of a thread; null — none. */
const openGroupKey = ref<string | null>(null);
const openGroup = computed(() => {
  const c = threadChips.value.find(x => x.key === openGroupKey.value);
  return c && c.kind === 'money' ? c : null;
});
function toggleGroup(key: string): void {
  showPast.value = false;
  openGroupKey.value = openGroupKey.value === key ? null : key;
}
function groupUnread(list: ReadonlyArray<Ticket>): number {
  return list.reduce((n, th) => n + (unreadByTicket.value[th.id] ?? 0), 0);
}
/** «80,00 USDT · Ждёт ответа» — one request of the group. */
function groupRowLabel(th: Ticket): string {
  const r = th.refId ? moneyRequests.value.get(th.refId) : undefined;
  if (!r) return threadLabel(th);
  return `${formatDisplayAmount(r.amount, r.asset, locale.value)} ${r.asset} · ${t(requestStateKey(r))}`;
}

/** The card-action requests behind CARD_CHANGE threads — their chips read «Заморозка карты ··1111». */
const cardActions = ref<Map<string, CardActionView>>(new Map());
watch(() => allThreads.value.filter(x => x.refType === 'CARD_CHANGE' && x.refId && !cardActions.value.has(x.refId)).length,
  missing => {
    if (!missing) return;
    cardActionsApi.mine()
      .then(list => { cardActions.value = new Map(list.map(r => [r.id, r])); })
      .catch(() => { /* the plain «Обслуживание карты» stays */ });
  });

async function selectThread(id: string) {
  showPast.value = false;
  openGroupKey.value = null;
  if (id === activeTicketId.value) return;
  activeTicketId.value = id;
  // Never show the previous thread's manager over this one: plain "Personal manager" until its first page says.
  leaveThread();
  await activate();
}

/**
 * Who the header shows (B2 §5.6a) — taken from every page the server sends, an empty poll included, so another
 * manager's reply or a removed photo reaches the open screen without a reload.
 */
/** The header last taken from this thread's pages — to notice that who answers has changed. */
let threadManager: ManagerView = DEFAULT_MANAGER;

/** @return whether the manager is different from the one this thread showed before */
function applyManager(page: MessagesPage): boolean {
  const m = page.manager;
  const changed = m.mode !== threadManager.mode || m.name !== threadManager.name
    || m.photoToken !== threadManager.photoToken;
  threadManager = m;
  emit('update:manager', m);
  return changed;
}

// ── The welcome (no conversation yet): asked when the screen opens and about once a minute while it stays open
// and visible (REQ-044); an older server (404) or a failure is the plain screen without suggestions (REQ-036).
const WELCOME_EVERY_MS = 60_000;
const welcomeManager = ref<ManagerView>(DEFAULT_MANAGER);
const welcomeSuggestions = ref<WelcomeSuggestion[]>([]);
/** The server has answered (or failed) once — before that the plain screen is only the starting state. */
const welcomeLoaded = ref(false);
let welcomeTimer: ReturnType<typeof setInterval> | null = null;

/** Only in the interface's language — the FAQ's hide-if-missing rule; no fallback to another language. */
const shownSuggestions = computed(() => welcomeSuggestions.value
  .map(s => ({ id: s.id, text: s.question[locale.value] ?? '' }))
  .filter(s => s.text.trim() !== '')
  .slice(0, 4));   // REQ-043: at most four, counted in this language (the server caps per language)

async function loadWelcome() {
  const gen = activationSeq;
  try {
    const w = await supportApi.welcome();
    if (gen !== activationSeq || ticket.value) return;
    welcomeManager.value = w.manager;
    welcomeSuggestions.value = w.suggestions;
  } catch {
    if (gen !== activationSeq || ticket.value) return;
    welcomeManager.value = DEFAULT_MANAGER;
    welcomeSuggestions.value = [];
  }
  welcomeLoaded.value = true;
  emit('update:manager', welcomeManager.value);
}

function startWelcome() {
  stopWelcome();
  welcomeLoaded.value = false;
  void loadWelcome();
  welcomeTimer = setInterval(() => {
    if (props.active && document.visibilityState === 'visible' && !ticket.value) void loadWelcome();
  }, WELCOME_EVERY_MS);
}

function stopWelcome() {
  if (welcomeTimer) clearInterval(welcomeTimer);
  welcomeTimer = null;
}

/** A suggestion fills the field (through the shared draft) — the client sends it, or edits it first. */
function useSuggestion(text: string) {
  // Never over what the client typed (or a prefilled draft); over an earlier suggestion it is a change of mind.
  const current = draft.value.trim();
  if (current && !shownSuggestions.value.some(s => s.text === current)) return;
  draft.value = text;
  void nextTick(() => inputEl.value?.focus());
}

/** A run of operator bubbles signed by the same manager gets the name once, over its first bubble (REQ-042). */
function startsSignedRun(i: number): boolean {
  const m = shownMessages.value[i];
  if (m.direction !== 'OPERATOR' || !m.operator) return false;
  const prev = i > 0 ? shownMessages.value[i - 1] : null;
  return !prev || prev.direction !== 'OPERATOR' || !prev.operator
    || prev.operator.name !== m.operator.name || prev.operator.photoToken !== m.operator.photoToken;
}
const sending = ref(false);
const initialLoading = ref(false);
const scrollEl = ref<HTMLElement | null>(null);

// Exponential backoff with jitter: first poll 5s, x2 up to a 60s cap; a new message resets it.
const POLL_MIN_MS = 5_000;
const POLL_MAX_MS = 60_000;
const POLL_JITTER = 0.2;
let currentInterval = POLL_MIN_MS;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let polling = false;                                            // R21 M10 — race guard

// attachmentId → object-URL, filled lazily for image attachments in the feed.
const attachmentUrls = reactive<Record<string, string>>({});

// Customer upload limits — mirror the server (SupportChatService): images + PDF + voice audio, ≤20MB per file,
// sent as is (CONCIERGE_CHAT_FIRST.md CF-037).
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const AUDIO_UPLOAD_TYPES = ['audio/webm', 'audio/ogg', 'audio/mp4', 'audio/mpeg'];
const ALLOWED_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf',
  ...AUDIO_UPLOAD_TYPES];
const fileInput = ref<HTMLInputElement | null>(null);
const pendingFile = ref<File | null>(null);
const fileError = ref('');
/** Why the armed request draft did not go (the concierge's own error text); empty — nothing to say. */
const intakeError = ref('');
const CHAT_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf,audio/webm,audio/ogg,audio/mp4,audio/mpeg';

// Voice recording (MediaRecorder). The round button sends the clip; Cancel throws it away.
const recording = ref(false);
const recordError = ref('');
let mediaRecorder: MediaRecorder | null = null;
let recordedChunks: Blob[] = [];
/** Set by Cancel: the recorder still fires `onstop`, which must then drop the clip instead of sending it. */
let discardRecording = false;
const recordSeconds = ref(0);
let recordTimer: ReturnType<typeof setInterval> | null = null;
const recordClock = computed(() => {
  const s = recordSeconds.value;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
});

const canSend = computed(() => (draft.value.trim() !== '' || pendingFile.value !== null)
  && !sending.value && !composerLocked.value);

// ── The one round button (DVL-004): send arrow when there is something to send, microphone otherwise ──
const showSendArrow = computed(() => recording.value || draft.value.trim() !== '' || pendingFile.value !== null);
const primaryDisabled = computed(() => {
  if (recording.value) return false;
  if (sending.value || composerLocked.value) return true;
  return showSendArrow.value ? !canSend.value : false;
});

function onPrimary() {
  if (recording.value) { stopRecording(); return; }
  if (showSendArrow.value) { void send(); return; }
  void startRecording();
}

/** Enter is a new line (as in mobile messengers); Ctrl/⌘+Enter sends from a desktop keyboard. */
function onEnter(e: KeyboardEvent) {
  if (!(e.ctrlKey || e.metaKey) || e.isComposing) return;
  e.preventDefault();
  if (canSend.value) void send();
}

// The field grows with the text up to ~5 lines (CSS max-height), then scrolls inside.
const inputEl = ref<HTMLTextAreaElement | null>(null);
function autoGrow() {
  const el = inputEl.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
  // Typing keeps the caret in view by itself; a draft put in from outside does not — show its end, where the caret is.
  if (document.activeElement !== el) el.scrollTop = el.scrollHeight;
}
// A draft set from outside (cleared after send, prefilled by "Top up") must resize the field too.
watch(draft, () => { void nextTick(autoGrow); });
// The client cleared the armed request draft: it is an ordinary chat message from now on.
watch(draft, (v) => {
  if (props.intakeArmed && v.trim() === '') { intakeError.value = ''; emit('update:intakeArmed', false); }
});
watch(() => props.intakeArmed, (armed) => { if (!armed) intakeError.value = ''; });

/**
 * A page of the open thread has been shown. The server marks what it serves as read (`SupportChatController`) and
 * reports how many were unread before that — so on screen nothing is unread any more: the chat shows no badge for
 * itself, and the shared counters (card-screen chip, FAB, thread chips) drop this thread at once. Don't wait for the
 * next poll: the client opens the chat and still sees a counter lit, which reads as "it didn't register".
 */
function markShownRead(n: number) {
  if (n <= 0) return;
  const id = ticket.value?.id;
  const counted = id ? unreadByTicket.value[id] ?? 0 : 0;
  if (id && counted) {
    const others = { ...unreadByTicket.value };
    delete others[id];
    unreadByTicket.value = others;
    recountUnread();
  }
  void refreshUnread();
}

// Only render inline (via window.open on a blob URL) the raster types the server treats as inline
// images (SupportChatService.INLINE_IMAGE_TYPES). NOT image/svg+xml: an SVG can carry <script>, and
// opening its same-origin blob URL as a top-level navigation would execute it (stored XSS — an operator
// upload can be any mime). Everything else falls through to downloadAttachment (<a download>, no nav).
const INLINE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
function isImage(a: Attachment) {
  return INLINE_IMAGE_TYPES.has(a.contentType);
}

// Voice messages (mirror SupportChatService.AUDIO_TYPES) → inline <audio> player.
const AUDIO_TYPES = new Set(AUDIO_UPLOAD_TYPES);
function isAudio(a: Attachment) {
  return AUDIO_TYPES.has(a.contentType);
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function loadAttachmentImages(list: Message[]) {
  if (!ticket.value) return;
  const tid = ticket.value.id;
  for (const m of list) {
    // Both inline images and voice audio need an object-URL (JWT endpoint — <img>/<audio src> can't send Bearer).
    if (m.attachment && (isImage(m.attachment) || isAudio(m.attachment)) && !attachmentUrls[m.attachment.id]) {
      supportAttachmentUrl(tid, m.attachment.id)
        .then(url => { attachmentUrls[m.attachment!.id] = url; })
        .catch(() => { /* bubble stays a file chip; tap still offers download */ });
    }
  }
}

async function openImage(a: Attachment) {
  if (!ticket.value) return;
  let url = attachmentUrls[a.id];
  if (!url) {
    url = await supportAttachmentUrl(ticket.value.id, a.id);
    attachmentUrls[a.id] = url;   // cache it so the inline thumbnail renders after the first tap too
  }
  window.open(url, '_blank', 'noopener');
}

async function downloadAttachment(a: Attachment) {
  if (!ticket.value) return;
  try {
    const url = await supportAttachmentUrl(ticket.value.id, a.id);
    const link = document.createElement('a');
    link.href = url;
    link.download = a.filename ?? 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch { /* transient network error — user can tap again */ }
}

function onFilePicked(event: Event) {
  fileError.value = '';
  intakeError.value = '';
  const file = (event.target as HTMLInputElement).files?.[0] ?? null;
  if (!file) return;
  if (props.intakeArmed) {
    // A task's document: the task's own rules (PDF or a picture, its size limits), not the chat's.
    const problem = intakeFileProblem(file, []);
    if (problem) { fileError.value = `${file.name}: ${t(problem)}`; clearPendingFile(); return; }
    pendingFile.value = file;
    return;
  }
  if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
    fileError.value = t('support.fileType');
    clearPendingFile();
    return;
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    fileError.value = t('support.fileTooBig');
    clearPendingFile();
    return;
  }
  pendingFile.value = file;
}

function clearPendingFile() {
  pendingFile.value = null;
  if (fileInput.value) fileInput.value.value = '';
}

// ── Voice recording (MediaRecorder) ──────────────────────────────────────────
// Pick a container the browser can record AND the server allowlists: opus/webm (Chrome/Android),
// mp4 (Safari/iOS). We strip the ";codecs=…" suffix for the File's mime so it matches AUDIO_TYPES.
function pickRecordMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(c)) return c;
  }
  return '';
}
function baseMime(m: string): string {
  return (m.split(';')[0] || 'audio/webm').trim();
}

async function startRecording() {
  recordError.value = '';
  fileError.value = '';
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    recordError.value = t('support.voice.unsupported');
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // The client left while the permission prompt was open: release the microphone, record nothing.
    if (unmounted) {
      stream.getTracks().forEach((tr) => tr.stop());
      return;
    }
    const mime = pickRecordMime();
    const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    recordedChunks = [];
    mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordedChunks.push(e.data); };
    mr.onstop = () => {
      stream.getTracks().forEach((tr) => tr.stop());
      recording.value = false;
      stopRecordClock();
      if (discardRecording) {          // Cancel — nothing is sent
        discardRecording = false;
        recordedChunks = [];
        return;
      }
      const type = baseMime(mr.mimeType || mime || 'audio/webm');
      const blob = new Blob(recordedChunks, { type });
      recordedChunks = [];
      if (blob.size === 0) { recordError.value = t('support.voice.empty'); return; }
      if (blob.size > MAX_ATTACHMENT_BYTES) { fileError.value = t('support.fileTooBig'); return; }
      const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
      // Messenger-style: the clip is sent the instant recording stops — no file chip, no manual "send".
      void sendVoice(new File([blob], `voice-${Date.now()}.${ext}`, { type }));
    };
    mediaRecorder = mr;
    discardRecording = false;
    mr.start();
    recording.value = true;
    recordSeconds.value = 0;
    recordTimer = setInterval(() => { recordSeconds.value++; }, 1000);
  } catch {
    recording.value = false;
    recordError.value = t('support.voice.micDenied');
  }
}

function stopRecordClock() {
  if (recordTimer) clearInterval(recordTimer);
  recordTimer = null;
}

/** Stop and send — the clip goes out the moment the recorder hands it over (messenger-style). */
function stopRecording() {
  mediaRecorder?.stop();
  mediaRecorder = null;
}

/** Stop and throw the clip away. */
function cancelRecording() {
  discardRecording = true;
  stopRecording();
}

/**
 * Bumped by every activation and every switch of thread: an answer that arrives for an older one (a slow first page,
 * a poll still in flight) is dropped instead of putting another thread's messages or manager on screen.
 */
let activationSeq = 0;

/** `silent` — a return to a kept-alive tab: what is on screen stays, the feed is re-read behind it (no spinner). */
async function activate(silent = false) {
  const gen = ++activationSeq;
  const stale = () => gen !== activationSeq;
  if (!silent) initialLoading.value = true;
  loadFailed.value = false;
  try {
    // Bound to a specific thread (a request's chat) → load exactly that ticket; else the general chat's
    // newest open ticket. Keeps per-request messages out of the general manager chat (P2).
    //
    // The general chat picks the GENERAL thread specifically — the same rule the backend applies when sending
    // (`SupportChatService.java:89-95`: a general message never lands in a bound thread and vice-versa).
    // This used to take the newest active thread of ANY type, so reading diverged from writing: the client read
    // a request's conversation while their reply went to GENERAL — a conversation they cannot see, and the
    // request's operator never received it (docs/BUGS_2026-08-16.md, B3+).
    // The conversation list is needed both for itself (the switcher) and to pick the default thread.
    // Every status (DVL-028): finished conversations go to the "past" list, live ones to the chips.
    const owned = await supportApi.myTickets().catch(() => [] as Ticket[]);
    if (stale()) return;
    allThreads.value = visibleThreads(owned);

    // Priority: the client's explicit pick → the component's binding to a request thread → the live GENERAL.
    // A finished GENERAL is never opened by default: a new message then starts a new conversation, and the old
    // one stays reachable from the past list.
    const chosen = activeTicketId.value ?? props.ticketId ?? null;
    const active = chosen
      ? allThreads.value.find(x => x.id === chosen)
        ?? await supportApi.getTicket(chosen).catch(() => null)
      : threads.value.find(x => x.refType === 'GENERAL') ?? null;
    if (stale()) return;
    if (chosen && !active) {
      // The thread asked for by id did not come back: an error with a retry, not the empty general chat (Б-7).
      ticket.value = null;
      messages.value = [];
      loadFailed.value = true;
      return;
    }
    ticket.value = active;
    activeTicketId.value = active?.id ?? null;
    if (active) {
      const page = await supportApi.messages(active.id);
      if (stale()) return;
      messages.value = page.items;
      loadAttachmentImages(page.items);
      markShownRead(page.unreadCount);
      applyManager(page);
      applyBlockStates(page, page.items);
      stopWelcome();
      await scrollToBottom();
    } else {
      messages.value = [];
      blockStates.value = {};
      if (!silent) emit('update:manager', DEFAULT_MANAGER);   // a silent re-read keeps the header until the welcome
      startWelcome();   // the welcome says who would reply
    }
  } catch {
    // Leave empty — the first send creates the thread. Not another thread's manager either. A thread asked for by id
    // that failed half-way is an error with a retry (Б-7), not an empty feed.
    if (!stale()) {
      emit('update:manager', DEFAULT_MANAGER);
      if (activeTicketId.value ?? props.ticketId) loadFailed.value = true;
    }
  } finally {
    if (!stale()) initialLoading.value = false;
  }
  if (stale()) return;   // the newer activation schedules its own poll
  if (loadFailed.value) return;   // nothing to poll until the retry loads the thread
  currentInterval = POLL_MIN_MS;
  schedulePoll();
}

/** A different thread is about to be shown: older answers are void, and the header is plain until it speaks. */
function leaveThread() {
  activationSeq++;
  stopWelcome();
  emit('update:manager', DEFAULT_MANAGER);
}

watch(() => props.active, (isActive) => {
  if (isActive) activate();
  else {
    stopPolling();
    // Closing the surface forgets the hand pick: next time it opens on its binding / the live GENERAL again.
    activeTicketId.value = null;
    showPast.value = false;
    leaveThread();
  }
});
// Re-bind when the target thread changes while the surface stays open (e.g. general → a request chat). The hand
// pick must yield, otherwise activate() would reload the previously picked thread instead of the new binding.
watch(() => props.ticketId, () => {
  activeTicketId.value = null;
  showPast.value = false;
  leaveThread();
  if (props.active) activate();
});

/**
 * Polling — and with it the server's "read" mark (DVL-016) — runs only while the page is actually visible: a chat
 * left open in a background tab must not tell the operator the client has seen the message.
 */
function onVisibility() {
  if (document.visibilityState === 'visible' && props.active && !parked) {
    currentInterval = POLL_MIN_MS;
    void pollOnce();
  }
}

let unmounted = false;
/**
 * The concierge tab is kept alive (App.vue): while another tab shows, the chat is parked — no polling, no welcome
 * refresh; back on screen it re-reads its feed silently, the messages already shown stay (owner, 02.11).
 */
let parked = false;
onDeactivated(() => {
  parked = true;
  stopPolling();
  stopWelcome();
});
onActivated(() => {
  if (!parked) return;   // the first activation is the mount itself
  parked = false;
  if (props.active) void activate(true);
});
onMounted(() => {
  document.addEventListener('visibilitychange', onVisibility);
  if (props.active) activate();
  void nextTick(autoGrow);   // a draft prefilled before mount ("Top up") sizes the field too
});
onUnmounted(() => {
  unmounted = true;
  document.removeEventListener('visibilitychange', onVisibility);
  stopPolling();
  stopWelcome();
  if (recording.value) cancelRecording();
});

function schedulePoll() {
  stopPolling();
  if (parked) return;   // resumed by onActivated
  const jitter = 1 + (Math.random() * 2 - 1) * POLL_JITTER;
  pollTimer = setTimeout(pollOnce, Math.round(currentInterval * jitter));
}

/**
 * Nothing open (no live GENERAL yet): look for a conversation the manager has just started (DVL-041) — slowly, the
 * thread list is all it needs. One appears → open it, as if the client had come in after it was written.
 */
async function pollForNewThread(): Promise<boolean> {
  const owned = await supportApi.myTickets();
  if (ticket.value || activeTicketId.value || props.ticketId) return false;   // the client opened something meanwhile
  allThreads.value = visibleThreads(owned);
  if (threads.value.some(x => x.refType === 'GENERAL')) {
    await activate();
    return true;
  }
  return false;
}

async function pollOnce() {
  if (!props.active) { pollTimer = null; return; }
  if (document.visibilityState !== 'visible') { pollTimer = null; return; }   // resumed by onVisibility
  if (polling) return;
  polling = true;
  if (!ticket.value) {
    try {
      if (await pollForNewThread()) return;   // activate() has scheduled its own poll
      currentInterval = Math.min(currentInterval * 2, POLL_MAX_MS);
    } catch {
      currentInterval = Math.min(currentInterval * 2, POLL_MAX_MS);
    } finally {
      polling = false;
    }
    schedulePoll();
    return;
  }
  // The answer belongs to the thread it was asked for: a switch meanwhile must not get the old thread's bubbles.
  const id = ticket.value.id;
  const gen = activationSeq;
  const wasPast = ticketIsPast.value;
  const last = messages.value[messages.value.length - 1];
  try {
    // A minute back from the newest bubble (§5.2): a message committed late is not skipped; what is shown is dropped.
    const page = await supportApi.messages(id, overlapCursor(last));
    // A switch or a reopen meanwhile: this page is for a thread no longer on screen (ticket.value still being the
    // old one until the new activation sets it, the id alone cannot tell).
    if (ticket.value?.id !== id || gen !== activationSeq) return;
    // An empty page too: the header follows who answers now. When that changed (a manager left, a photo was
    // removed), the bubbles already on screen are re-read: their signatures come with them, and a leaver's name must
    // go from those too (E10), not only from new bubbles.
    let newItems = unseen(messages.value, page.items);
    const fresh = newItems.length > 0;
    applyBlockStates(page, newItems);
    if (applyManager(page)) {
      // The same first read the activation does — it replaces what is on screen, new bubbles included.
      const full = await supportApi.messages(id);
      if (ticket.value?.id !== id || gen !== activationSeq) return;
      messages.value = full.items;
      loadAttachmentImages(full.items);
      applyBlockStates(full);
      newItems = [];
    }
    // A finished conversation the manager has reopened: refresh its status so the "completed" note goes away.
    if (wasPast && fresh) {
      const fresh = await supportApi.getTicket(id).catch(() => null);
      if (fresh && ticket.value?.id === id) {
        ticket.value = fresh;
        allThreads.value = allThreads.value.map(x => (x.id === id ? fresh : x));
      }
    }
    if (fresh) {
      messages.value.push(...newItems);
      loadAttachmentImages(newItems);
      currentInterval = POLL_MIN_MS;
      markShownRead(page.unreadCount);
      await scrollToBottom();
    } else {
      // A task's screen polls every 5 s (§5.2): its cards change state without a new message. So does a thread with
      // a money request still moving — its chip would otherwise lag the money by up to a minute.
      currentInterval = props.taskMode || hasMovingBlock(blockStates.value)
        ? POLL_MIN_MS : Math.min(currentInterval * 2, POLL_MAX_MS);
    }
  } catch {
    currentInterval = Math.min(currentInterval * 2, POLL_MAX_MS);
  } finally {
    polling = false;
  }
  schedulePoll();
}

function stopPolling() {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
}

async function send() {
  if (!canSend.value) return;
  if (props.intakeArmed && props.intakeSubmit) { await sendIntake(props.intakeSubmit); return; }
  sending.value = true;
  const text = draft.value.trim();
  let ticketId = ticket.value?.id ?? null;
  try {
    // Attachment goes first, text follows as its own message (two bubbles, like messengers).
    //
    const bound = openThreadTarget();
    if (pendingFile.value) {
      const r = await supportApi.sendAttachment(pendingFile.value, bound);
      ticketId = r.ticketId;
      clearPendingFile();
    }
    if (text) {
      const r = await supportApi.send(text, { ticketId: bound });
      ticketId = r.ticketId;
      draft.value = '';
    }
    if (!ticketId) return;
    await refreshFeed(ticketId);
  } catch {
    /* not sent — draft/file stays in the composer */
  } finally {
    sending.value = false;
  }
}

/** The armed draft files a task (rich format) with the attached file; the host opens it. Errors stay under the field. */
async function sendIntake(submit: (text: string, files: File[]) => Promise<string | null>) {
  sending.value = true;
  intakeError.value = '';
  try {
    const error = await submit(draft.value.trim(), pendingFile.value ? [pendingFile.value] : []);
    if (error) { intakeError.value = error; return; }
    clearPendingFile();
    draft.value = '';
    emit('update:intakeArmed', false);
  } finally {
    sending.value = false;
  }
}

/**
 * Where a message goes: exactly the thread that is open on screen — including one picked from the conversation
 * list or the past list. Sending by `props.ticketId` would bring back the very bug B3+ fixes (the client reads one
 * conversation while the reply goes to another).
 *
 * The open thread is named explicitly even when it is GENERAL: a finished GENERAL is reopened by a reply only
 * when the server is told which thread (DVL-028); an untargeted post would start a new conversation the client is
 * not looking at. Untargeted (undefined) only when nothing is open yet — the first message opens a GENERAL thread.
 */
function openThreadTarget(): string | undefined {
  return ticket.value?.id ?? props.ticketId ?? undefined;
}

/** Reload the thread + attachments after a successful send and resume fast polling. */
async function refreshFeed(ticketId: string) {
  // The ticket itself too: a reply to a finished conversation has just reopened it (DVL-028).
  ticket.value = await supportApi.getTicket(ticketId);
  activeTicketId.value = ticketId;
  stopWelcome();   // the first message opened a conversation: the header now follows its pages
  const fresh = ticket.value;
  allThreads.value = allThreads.value.some(x => x.id === ticketId)
    ? allThreads.value.map(x => (x.id === ticketId ? fresh : x))
    : [fresh, ...allThreads.value];
  const page = await supportApi.messages(ticketId);
  messages.value = page.items;
  loadAttachmentImages(page.items);
  markShownRead(page.unreadCount);
  applyManager(page);
  applyBlockStates(page);
  currentInterval = POLL_MIN_MS;
  schedulePoll();
  await scrollToBottom();
}

/**
 * Upload a recorded clip immediately (messenger-style, on stop) — no attachment chip, no manual send.
 * On failure it falls back to the pending-file chip so the recording isn't lost.
 */
async function sendVoice(file: File) {
  if (sending.value) return;
  sending.value = true;
  try {
    // Same thread as text — the one on screen (it used to be `props.ticketId`, so a clip recorded in a thread
    // picked from the list went to the general chat).
    const r = await supportApi.sendAttachment(file, openThreadTarget());
    if (r.ticketId) await refreshFeed(r.ticketId);
  } catch {
    pendingFile.value = file;
  } finally {
    sending.value = false;
  }
}

/**
 * The cards grow once the task they read arrives (after the feed has scrolled): a reader at the bottom stays there.
 */
watch(() => props.task, () => {
  const el = scrollEl.value;
  if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 160) void scrollToBottom();
});

async function scrollToBottom() {
  await nextTick();
  if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight;
}
</script>

<style scoped lang="scss">
.support-chat { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.support-chat__faq-link {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  width: 100%; padding: 12px 16px; cursor: pointer;
  background: var(--bg-card); border: none; border-bottom: 1px solid var(--hairline);
  color: var(--text); font: inherit; font-size: 14px; text-align: left;
}
.support-chat__faq-chevron { color: var(--gold); font-size: 18px; }
/* Conversation switcher (B1/B3). Scrolls horizontally so a third thread doesn't break the header on narrow screens. */
.support-chat__threads {
  display: flex; gap: 6px;
  padding: 8px 12px;
  overflow-x: auto;
  background: var(--bg-card); border-bottom: 1px solid var(--hairline);
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}
.support-chat__thread {
  display: inline-flex; align-items: center; gap: 6px;
  flex: 0 0 auto;
  padding: 6px 12px; border-radius: 100px;
  background: var(--bg-elev); border: 1px solid var(--hairline);
  color: var(--text-2); font: inherit; font-size: 12.5px; cursor: pointer;
  white-space: nowrap;
}
.support-chat__thread--active {
  border-color: var(--gold);
  color: var(--text);
}
.support-chat__thread-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 9px;
  background: var(--red, var(--red));
  color: #fff;
  font-size: 10.5px; font-weight: 600;
}
.support-chat__group-hint { padding: 0 2px 4px; color: var(--text-3); font-size: 12px; }
.support-chat__body {
  flex: 1; overflow-y: auto; padding: 16px;
  display: flex; flex-direction: column; gap: 10px;
}
.support-chat__loading { display: grid; place-items: center; padding: 40px 0; }
.spinner {
  width: 26px; height: 26px;
  border: 3px solid var(--gold);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.support-chat__empty { color: var(--text-3); font-size: 13px; margin: 0; }
/* The welcome (B2 REQ-043). */
.welcome { display: flex; flex-direction: column; gap: 12px; }
/* Prototype D welcome (concierge-messenger.css, computed at 390 / 1440): centred in the free space; Garamond 32 / 36,
   18 px to «Чем можем помочь?» (Inter 16 muted), 28 px to the pills — Inter 500 12, 12px 16px, r24, the suggest
   surface with a hairline bronze border, 10 px apart. */
.welcome--greeting {
  flex: 1; justify-content: center; align-items: center; gap: 0; text-align: center;
  padding: 24px 20px;
}
.welcome__greeting {
  margin: 0 0 18px; font-family: var(--font-heading); font-weight: 400; font-size: 32px; line-height: 1.15;
  letter-spacing: -0.02em; color: var(--text);
  @media (min-width: 700px) { font-size: 36px; }
}
.welcome__question { margin: 0 0 28px; font-size: 16px; line-height: 1.5; color: var(--muted); }
.welcome__prompts { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; max-width: 560px; }
.welcome__prompts :deep(button), .welcome__prompt {
  display: inline-flex; align-items: center; gap: 8px; padding: 12px 16px; border-radius: 24px; cursor: pointer;
  background: var(--suggest-bg); border: 1px solid #8b73551a; color: var(--text);
  box-shadow: 0 3px 8px -3px #30251a12, inset 0 1px 0 #fff;
  font: inherit; font-size: 12px; line-height: 1.5; font-weight: 500;
  :deep(svg) { color: var(--bronze); flex: none; }
}
.welcome__title {
  margin: 0; font-family: var(--font-heading); font-weight: 400; font-size: 27px; letter-spacing: -0.01em;
  color: var(--text);
}
.welcome__manager {
  display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 14px;
  background: var(--bg-card); border: 1px solid var(--hairline); color: var(--text); font-size: 14px;
  overflow-wrap: anywhere;
}
.welcome__label {
  font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--text-3); font-weight: 500;
}
.welcome__suggestions { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.welcome__suggestion {
  padding: 8px 14px; border-radius: 100px; cursor: pointer; text-align: left;
  background: var(--bg-card); border: 1px solid var(--hairline-strong); color: var(--text);
  font: inherit; font-size: 13.5px; line-height: 1.3;
}
/* Inside the welcome the channels are a card like the manager's, not the full-width strip of a conversation. */
.welcome__contacts { padding: 10px 14px; border: 1px solid var(--hairline); border-radius: 14px; }
.support-chat__messages { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.msg {
  max-width: 80%;
  padding: 8px 12px; border-radius: 14px;
  display: flex; flex-direction: column; gap: 2px;
  font-size: 14px; line-height: 1.35;
}
.msg__text { white-space: pre-wrap; word-break: break-word; }
.msg__author { font-size: 11.5px; font-weight: 600; color: var(--gold); overflow-wrap: anywhere; }
.msg__time { font-size: 10px; color: var(--text-3); align-self: flex-end; }
.msg__image {
  display: block; max-width: 100%; max-height: 240px;
  border-radius: 10px; cursor: pointer;
}
.msg__file {
  display: inline-flex; align-items: baseline; gap: 6px;
  background: none; border: none; padding: 0; cursor: pointer;
  color: inherit; font: inherit; text-align: left;
  text-decoration: underline;
}
.msg__file-name { word-break: break-all; }
.msg__file-size { font-size: 10px; opacity: 0.7; }
/* Prototype bubbles: outgoing on --soft with dark text, incoming white with a hairline (03 §6, ChatBubble). */
.msg--customer { align-self: flex-end; background: var(--soft); color: var(--text); border-bottom-right-radius: 4px; }
.msg--customer .msg__time { color: var(--muted); }
.msg--operator { align-self: flex-start; background: var(--ds-surface); color: var(--text); border: 1px solid var(--line); border-bottom-left-radius: 4px; }
/* Cards (CONCIERGE_CHAT_FIRST.md §5.2): wider than a bubble, framed; a system line sits centred without a bubble. */
.msg--card { max-width: 92%; border: 1px solid var(--hairline); }
.msg-system { align-self: center; display: flex; justify-content: center; max-width: 100%; }

/* Past conversations (DVL-028). */
.support-chat__past { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.support-chat__past-row {
  width: 100%; display: flex; flex-direction: column; align-items: flex-start; gap: 2px;
  padding: 12px 14px; border-radius: 14px; cursor: pointer; text-align: left;
  background: var(--bg-card); border: 1px solid var(--hairline); color: var(--text); font: inherit;
}
.support-chat__past-label { font-size: 14px; }
.support-chat__past-meta { font-size: 12px; color: var(--text-3); }
.support-chat__past-note {
  margin: 0; padding: 8px 12px; border-radius: 12px; font-size: 12.5px;
  background: var(--bg-card); border: 1px dashed var(--hairline-strong); color: var(--text-2);
}

/* Composer (DVL-004): pending-file chip, a pill field with the paperclip inside, one round button. */
.support-chat__pending {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  padding: 8px 12px 0; font-size: 12px; color: var(--text);
}
.support-chat__chip {
  display: inline-flex; align-items: center; gap: 6px; max-width: 100%;
  padding: 4px 4px 4px 10px; border-radius: 100px;
  background: var(--bg-card); border: 1px solid var(--hairline);
}
.support-chat__chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 60vw; }
.support-chat__chip-size { color: var(--text-3); font-size: 11px; }
.support-chat__chip-clear {
  width: 24px; height: 24px; border-radius: 50%; border: none; cursor: pointer;
  background: var(--bg-elev); color: var(--text-2); font-size: 15px; line-height: 1;
}
.support-chat__file-error { color: var(--red); }
.support-chat__file-input { display: none; }
.support-chat__compose {
  display: flex; align-items: flex-end; gap: 8px;
  padding: 8px 12px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--hairline);
}
.support-chat__pill, .support-chat__recbar {
  flex: 1; min-width: 0; min-height: 44px;
  display: flex; align-items: flex-end; gap: 2px;
  padding: 2px 12px 2px 4px; border-radius: 22px;
  background: var(--bg-card); border: 1px solid var(--hairline);
}
.support-chat__pill--disabled { opacity: 0.6; }
.support-chat__icon-btn {
  flex: none; width: 40px; height: 40px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: none; border: none; padding: 0; cursor: pointer; color: var(--gold);
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.support-chat__input {
  flex: 1; min-width: 0; resize: none;
  /*
   * One line by default, up to 5 lines (5 × 20px), then it scrolls inside. The space around the text is a MARGIN,
   * not padding: a textarea's padding scrolls away with its content, so the old 120px box with 10px padding showed
   * five lines plus half of an earlier one cut at the top edge. Without padding the scrolled viewport is always a
   * whole number of lines, and the margin keeps the text off the pill's rounded edge.
   */
  min-height: 20px; max-height: 100px; overflow-y: auto;
  margin: 10px 0; padding: 0; border: none; outline: none; background: transparent;
  color: var(--text); font-family: inherit; font-size: 15px; line-height: 20px;
  overflow-wrap: break-word; word-break: normal;
  &::placeholder { color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
}
.support-chat__recbar { align-items: center; gap: 10px; padding: 2px 14px 2px 6px; }
.support-chat__rec-cancel {
  min-height: 40px; padding: 0 10px; border: none; background: none; cursor: pointer;
  color: var(--text-2); font: inherit; font-size: 14px;
}
.support-chat__rec-dot {
  width: 10px; height: 10px; border-radius: 50%; background: var(--red, var(--red));
  animation: support-chat-rec-pulse 1.2s ease-in-out infinite;
}
.support-chat__rec-time { font-variant-numeric: tabular-nums; font-size: 14px; color: var(--text); }
@keyframes support-chat-rec-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
.support-chat__round {
  flex: none; width: 44px; height: 44px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid var(--hairline); background: var(--bg-card); color: var(--gold);
  cursor: pointer; padding: 0;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.support-chat__round--send { background: var(--gold); border-color: var(--gold); color: var(--text-inverse); }

/*
 * A concierge task's feed — the owner's design (docs/design-2026-10-24, `.concierge-messages`, `.bubble`,
 * `.case-terms`, `.concierge-composer`; phone metrics). Light theme only, its own palette.
 */
.support-chat--task {
  background: var(--ds-surface); color: var(--onyx);
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; -webkit-font-smoothing: antialiased;
  .support-chat__body { padding: 18px 16px; gap: 14px; background: var(--ds-surface); }
  .support-chat__messages { gap: 14px; }
  .msg {
    max-width: 90%; width: fit-content; padding: 13px 15px; gap: 0;
    font-size: 12px; line-height: 1.65; flex-shrink: 0;
  }
  .msg--operator {
    background: var(--ds-surface); color: var(--onyx); border: 1px solid var(--line); border-radius: 16px 16px 16px 4px;
  }
  .msg--customer {
    background: var(--soft); color: var(--text); border: 0; border-radius: 16px 16px 4px 16px;
  }
  /* A card: the design's `.case-terms` — full width (up to 430px), white, 14px corners. */
  .msg--card {
    width: 100%; max-width: 430px; padding: 17px; border-radius: 14px;
    background: var(--ds-surface); border: 1px solid var(--line);
  }
  .msg__text { white-space: pre-wrap; overflow-wrap: anywhere; }
  .msg__by { display: block; font-size: 9px; line-height: 1.4; color: var(--muted); text-align: right; margin-top: 7px; }
  .msg--customer .msg__by { color: var(--muted); }   /* the customer bubble is --soft now: the signature takes the muted text colour */
  /* An attachment: the design's `.concierge-file` chip inside the bubble. */
  .msg__file {
    display: flex; align-items: center; gap: 10px; padding: 11px 12px; border-radius: 10px; text-decoration: none;
    background: rgba(255, 255, 255, 0.6); border: 1px solid var(--line); color: var(--onyx); font-size: 11px;
  }
  .msg__file-size { color: var(--muted); }
  .support-chat__pending { background: var(--ds-surface); border-top: 1px solid var(--line); padding: 8px 16px 0; }
}
.msg__link { color: var(--bronze-text); text-decoration: underline; }
</style>
